import { preload } from '../nojq/_load.js';

/**
 * Cached Promise to ensure Chart library is loaded only once.
 * @type {Promise<typeof window.Chart> | null}
 */
let chartPromise = null;

/**
 * Registers a `zerobreak` cartesian scale (Chart.js 2.9 has no broken axis).
 *
 * $0 stays on the axis at the very bottom, the empty range between $0 and
 * `breakValue` is collapsed into the bottom `breakRatio` of the plot, and
 * everything above `breakValue` keeps a plain linear scale so the data shape is
 * not distorted. The scale draws its own axis line, split by a break mark
 * (`breakMark`) sitting just above the zero tick.
 *
 * @param {typeof window.Chart} Chart
 */
function registerZeroBreakScale(Chart) {
    const LinearScale = Chart.scaleService.getScaleConstructor('linear');

    const ZeroBreakScale = LinearScale.extend({
        buildTicks: function () {
            LinearScale.prototype.buildTicks.call(this);
            const breakValue = this.options.breakValue;
            // One tick for $0, the rest spread evenly over the real data band.
            const count = Math.max(2, (this.options.ticks.maxTicksLimit || 6) - 1);
            const step = (this.max - breakValue) / (count - 1);
            const ticks = [this.min];
            for (let i = 0; i < count; i++) {
                ticks.push(Math.round(breakValue + step * i));
            }
            this.ticks = this.isHorizontal() ? ticks : ticks.reverse();
        },
        getPixelForValue: function (value) {
            const me = this;
            const { breakValue, breakRatio } = me.options;
            const parsed = +me.getRightValue(value);
            const decimal = parsed <= breakValue ? (Math.max(parsed, me.min) / (breakValue || 1)) * breakRatio : breakRatio + ((parsed - breakValue) / (me.max - breakValue)) * (1 - breakRatio);
            return me.getPixelForDecimal(decimal);
        },
        getValueForPixel: function (pixel) {
            const me = this;
            const { breakValue, breakRatio } = me.options;
            const decimal = me.getDecimalForPixel(pixel);
            return decimal <= breakRatio ? (decimal / breakRatio) * breakValue : breakValue + ((decimal - breakRatio) / (1 - breakRatio)) * (me.max - breakValue);
        },
        draw: function (chartArea) {
            LinearScale.prototype.draw.call(this, chartArea);

            const me = this;
            const ctx = me.ctx;
            const { breakValue, breakMark } = me.options;
            if (!ctx || breakValue <= me.min) {
                return;
            }

            const originY = me.getPixelForValue(me.min);
            const resumeY = me.getPixelForValue(breakValue);
            // Shrinks when the collapsed band is short, so the mark never reaches the data.
            const radius = Math.min(breakMark.radius, (originY - resumeY - breakMark.offset * 2) / (breakMark.humps * 2));
            if (radius <= 0) {
                return;
            }

            const axisX = me.position === 'left' ? me.right : me.left;
            const markBottom = originY - breakMark.offset;
            const markTop = markBottom - radius * breakMark.humps * 2;

            ctx.save();
            ctx.strokeStyle = breakMark.color;
            ctx.lineWidth = breakMark.lineWidth;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(axisX, me.top);
            ctx.lineTo(axisX, markTop);
            ctx.moveTo(axisX, markBottom);
            ctx.lineTo(axisX, me.bottom);
            ctx.moveTo(axisX, markTop);
            for (let i = 0; i < breakMark.humps; i++) {
                // Half circles bulging right, left, right — each starts where the previous one ended.
                ctx.arc(axisX, markTop + radius * (i * 2 + 1), radius, -Math.PI / 2, Math.PI / 2, i % 2 === 1);
            }
            ctx.stroke();
            ctx.restore();
        },
    });

    const linearDefaults = Chart.scaleService.getScaleDefaults('linear');
    Chart.scaleService.registerScaleType('zerobreak', ZeroBreakScale, {
        ...linearDefaults,
        // The scale draws its own axis line, split around the break mark.
        gridLines: { ...linearDefaults.gridLines, drawBorder: false },
        breakValue: 0,
        /** Share of the plot height given to the collapsed $0 → breakValue band, sized to host the break mark. */
        breakRatio: 0.2,
        breakMark: {
            color: 'rgba(0, 0, 0, 0.25)',
            lineWidth: 1.5,
            /** Half-circle radius; the squiggle is `humps * 2 * radius` tall and `2 * radius` wide. */
            radius: 4,
            humps: 3,
            /** Gap between the zero tick and the bottom of the squiggle. */
            offset: 6,
        },
    });
}

/**
 * Loads the Chart.js library (via a custom build at `_chart.js`), registers
 * a center-text plugin, and resolves with the loaded Chart constructor.
 * Subsequent calls return the same resolved Promise.
 *
 * @returns {Promise<typeof window.Chart>}
 */
export function loadChart() {
    if (!chartPromise) {
        chartPromise = preload({
            files: [window.__INITIAL_STATE__.staticUrl + 'javascripts/desktop/libs/_chart.js'],
            type: 'js',
        })
            .then(() => {
                // Register a plugin that draws centered text inside a doughnut/pie chart
                window.Chart.pluginService.register({
                    beforeDraw: function (chart) {
                        const centerConfig = chart.config.options.elements?.center;
                        if (!centerConfig) {
                            return;
                        }

                        const ctx = chart.chart.ctx;
                        const txt = centerConfig.text || '';
                        const fontStyle = centerConfig.fontStyle || 'Arial';
                        const color = centerConfig.color || '#000';
                        const maxFontSize = centerConfig.maxFontSize || 75;
                        const sidePadding = centerConfig.sidePadding || 20; // as percentage
                        const sidePaddingCalculated = (sidePadding / 100) * (chart.innerRadius * 2);

                        // Start with a base font of 30px
                        ctx.font = '30px ' + fontStyle;

                        // Measure text width and available width
                        const stringWidth = ctx.measureText(txt).width;
                        const elementWidth = chart.innerRadius * 2 - sidePaddingCalculated;
                        const widthRatio = elementWidth / stringWidth;
                        const newFontSize = Math.floor(30 * widthRatio);
                        const elementHeight = chart.innerRadius * 2;

                        // Choose font size not exceeding element height or maxFontSize
                        let fontSizeToUse = Math.min(newFontSize, elementHeight, maxFontSize);
                        const minFontSize = centerConfig.minFontSize ?? 20;
                        const lineHeight = centerConfig.lineHeight || 25;

                        let wrapText = false;
                        if (fontSizeToUse < minFontSize) {
                            fontSizeToUse = minFontSize;
                            wrapText = true;
                        }

                        // Set text alignment
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.font = fontSizeToUse + 'px ' + fontStyle;
                        ctx.fillStyle = color;

                        // Determine center point
                        let centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
                        let centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;

                        if (!wrapText) {
                            ctx.fillText(txt, centerX, centerY);
                            return;
                        }

                        // Wrap text into multiple lines if needed
                        const words = txt.split(' ');
                        let line = '';
                        const lines = [];

                        for (let n = 0; n < words.length; n++) {
                            const testLine = line + words[n] + ' ';
                            const metrics = ctx.measureText(testLine);
                            const testWidth = metrics.width;
                            if (testWidth > elementWidth && n > 0) {
                                lines.push(line);
                                line = words[n] + ' ';
                            } else {
                                line = testLine;
                            }
                        }
                        lines.push(line);

                        // Adjust vertical starting point
                        centerY -= (lines.length / 2) * lineHeight;

                        // Draw each line
                        lines.forEach((l) => {
                            ctx.fillText(l, centerX, centerY);
                            centerY += lineHeight;
                        });
                    },
                });

                registerZeroBreakScale(window.Chart);

                return window.Chart;
            })
            .catch((err) => {
                // If preload fails, reset chartPromise so that future attempts can retry
                chartPromise = null;
                return Promise.reject(err);
            });
    }

    return chartPromise;
}
